import { CalendarIcon, LocationMarkerIcon } from "@heroicons/react/solid";
import { backoffice, useAuth } from "context/auth-context";
import Router from "next/router";
import { useTranslation } from "next-i18next";
import { type ReactNode } from "react";
import { PERMISSIONS, type POS } from "utils/auth-client";

function PosItem({ name, address, cashDrawers, onClick }: POS & { onClick: () => void }) {
  const { t } = useTranslation();
  const open = cashDrawers.length > 0;

  return (
    <li>
      <button
        className="flex w-full flex-col justify-between space-y-2 px-4 py-4 transition duration-150 ease-in-out hover:bg-gray-50 focus:bg-gray-50 focus:outline-none sm:flex-row sm:space-y-0 sm:px-6"
        onClick={onClick}
      >
        <div className="flex flex-col items-start justify-center space-y-2">
          <h2 className="truncate text-sm font-medium leading-5 text-primary-600">{name}</h2>
          {address?.street ? (
            <div className="flex items-center text-sm leading-5 text-gray-500">
              <LocationMarkerIcon className="mr-1.5 h-5 w-5 text-gray-400" />
              {address.street}
            </div>
          ) : null}
        </div>
        {open ? (
          <div className="flex space-x-2 sm:flex-col sm:space-x-0 sm:space-y-2">
            <span className="inline-flex justify-center rounded-full bg-green-100 px-2 text-xs font-semibold leading-5 text-green-800">
              {t("cashDrawer.opened")}
            </span>
            <div className="flex items-center text-sm leading-5 text-gray-500">
              <CalendarIcon className="mr-1 h-4 w-4 text-gray-400" />
              <time>{cashDrawers[0]?.getStartDate()}</time>
            </div>
          </div>
        ) : null}
      </button>
    </li>
  );
}

function PosList({ children }: { children: ReactNode }) {
  return (
    <ul className="w-full divide-y overflow-hidden bg-white shadow sm:rounded-md">{children}</ul>
  );
}

function SelectPos() {
  const { t } = useTranslation();
  const { setPos, user, hasPermissions } = useAuth();

  const isBackoffice = hasPermissions([PERMISSIONS.BACK_OFFICE]);
  const hasPos = isBackoffice && (user?.pos.length ?? 0) > 0;

  return (
    <div className="mx-auto flex w-full flex-col items-center justify-center px-4 py-12 sm:max-w-lg sm:px-6 lg:px-8">
      <h1 className="py-5 text-center text-xl text-gray-800">{t("layout.selectPos")}</h1>
      {isBackoffice ? (
        <PosList>
          <PosItem
            {...backoffice}
            onClick={() => {
              setPos(backoffice);
            }}
          />
        </PosList>
      ) : null}
      {hasPos ? <div className="my-5 w-full border-b border-gray-500/40" /> : null}
      <PosList>
        {user?.pos.map((pos) => (
          <PosItem
            key={pos.id}
            {...pos}
            onClick={() => {
              setPos(pos);
              void Router.push("/");
            }}
          />
        ))}
      </PosList>
    </div>
  );
}

export default SelectPos;
